/* SUMMARY
  * Imports
  * UnauthorizedAccess: Page affichée pour les utilisateurs ROLE_TECH
*/

/* Imports */
import { useEffect, useState, useCallback } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { Typography } from "@material-tailwind/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
import { removeCookie } from "../../services/auth/cookies";
/***/

const COUNTDOWN_SECONDS = 10;

/**
 * UnauthorizedAccess: Page affichée pour les utilisateurs avec uniquement le rôle ROLE_TECH.
 * Ces utilisateurs n'ont pas accès à la plateforme web, uniquement à l'application mobile.
 * Un compteur déconnecte automatiquement l'utilisateur après X secondes.
 */
export default function UnauthorizedAccess() {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const { keycloak } = useKeycloak();

  const handleLogout = useCallback(() => {
    // Supprimer les cookies d'authentification
    removeCookie("userRoles");
    removeCookie("userId");
    
    // Supprimer les données du localStorage liées à l'auth
    localStorage.removeItem("realm");
    
    // Déconnecter via Keycloak
    keycloak.logout({ redirectUri: window.location.origin });
  }, [keycloak]);

  useEffect(() => {
    if (countdown <= 0) {
      handleLogout();
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, handleLogout]);

  return (
    <div className="h-screen flex items-center justify-center flex-col gap-6 bg-gray-50 px-4">
      <div className="flex flex-col items-center gap-4 max-w-md text-center">
        {/* Icône d'avertissement */}
        <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-orange-500" />
        </div>

        {/* Titre */}
        <Typography variant="h3" className="text-gray-800">
          {t("error:unauthorizedAccess:title")}
        </Typography>

        {/* Message principal */}
        <Typography className="text-gray-600">
          {t("error:unauthorizedAccess:message")}
        </Typography>

        {/* Message secondaire */}
        <Typography className="text-gray-500 text-sm">
          {t("error:unauthorizedAccess:mobileOnly")}
        </Typography>

        {/* Compteur */}
        <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
          <Typography className="text-orange-700">
            {t("error:unauthorizedAccess:countdown", { seconds: countdown })}
          </Typography>
        </div>

        {/* Bouton de déconnexion manuelle */}
        <button
          onClick={handleLogout}
          className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          {t("error:unauthorizedAccess:logoutNow")}
        </button>
      </div>
    </div>
  );
}
